// REMIX HMR BEGIN
if (!window.$RefreshReg$ || !window.$RefreshSig$ || !window.$RefreshRuntime$) {
  console.warn('remix:hmr: React Fast Refresh only works when the Remix compiler is running in development mode.');
} else {
  var prevRefreshReg = window.$RefreshReg$;
  var prevRefreshSig = window.$RefreshSig$;
  window.$RefreshReg$ = (type, id) => {
    window.$RefreshRuntime$.register(type, "\"app\\\\root.tsx\"" + id);
  }
  window.$RefreshSig$ = window.$RefreshRuntime$.createSignatureFunctionForTransform;
}
var _s = $RefreshSig$(),
  _s2 = $RefreshSig$(),
  _s3 = $RefreshSig$(),
  _s4 = $RefreshSig$();
import * as __hmr__ from "remix:hmr";
if (import.meta) {
  import.meta.hot = __hmr__.createHotContext(
  //@ts-expect-error
  "app\\root.tsx");
}
// REMIX HMR END

import { Links, Meta, Outlet, Scripts, ScrollRestoration, isRouteErrorResponse, useLoaderData, useRouteError } from "@remix-run/react";
import { json } from "@remix-run/node";
import { randomBytes } from "crypto";
import { createContext, useContext } from "react";
import stylesheet from "./styles/tailwind.css";
import { getCsrfToken } from "./utils/csrf.server";
import { ThemeProvider } from "./utils/theme";
export const links = () => [{
  rel: "stylesheet",
  href: stylesheet
}, {
  rel: "icon",
  type: "image/png",
  href: "/logo.png"
}];
const NonceContext = createContext("");
export const useNonce = () => {
  _s();
  return useContext(NonceContext);
};

// Security headers are forwarded from the loader (which embeds the per-request nonce).
_s(useNonce, "gDsCjeeItUuvgOWf1v4qoK9RF6k=");
export const headers = ({
  loaderHeaders
}) => ({
  ...Object.fromEntries(loaderHeaders.entries())
});
export async function loader({
  request
}) {
  const {
    token,
    cookie
  } = await getCsrfToken(request);
  const nonce = randomBytes(16).toString("base64");
  const csp = ["default-src 'self'",
  // nonce covers Remix hydration scripts + the inline theme script
  `script-src 'self' 'nonce-${nonce}'`, "style-src 'self' 'unsafe-inline'", "connect-src 'self' https://compliance-api.ramadpay.com", "img-src 'self' data: blob:", "font-src 'self'", "frame-ancestors 'none'", "form-action 'self'", "base-uri 'self'"].join("; ");
  return json({
    csrfToken: token,
    nonce
  }, {
    headers: {
      "Set-Cookie": cookie,
      "X-Frame-Options": "DENY",
      "X-Content-Type-Options": "nosniff",
      "X-XSS-Protection": "1; mode=block",
      "Referrer-Policy": "strict-origin-when-cross-origin",
      "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
      "Cache-Control": "no-store",
      "Content-Security-Policy": csp
    }
  });
}
export function Layout({
  children
}) {
  _s2();
  // During SSR the nonce comes from the loader; during hydration it's already
  // in the DOM so the attribute value is preserved by React.
  const data = useLoaderData();
  const nonce = data?.nonce ?? "";
  return <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <script nonce={nonce} dangerouslySetInnerHTML={{
        __html: `try{if(localStorage.getItem('theme')==='dark'){document.documentElement.classList.add('dark')}}catch(e){}`
      }} />
      </head>
      <body>
        {children}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>;
}

// Catches anything thrown by a route that has no ErrorBoundary of its own.
// Without this, such a throw renders the browser's own blank 500 page and the
// message never reaches the user: an analyst opening a page their role cannot
// see got an unexplained 500 rather than the "Access Denied" text the loader
// had already written.
//
// Remix renders this inside the root Layout, so it returns page content only,
// not a document. Layout reads the loader data defensively because that data is
// absent when the failure is in the root loader itself.
_s2(Layout, "5thj+e1edPyRpKif1JmVRC6KArE=", false, function () {
  return [useLoaderData];
});
_c = Layout;
export function ErrorBoundary() {
  _s3();
  const error = useRouteError();
  let title = "Something went wrong";
  let detail = "";
  if (isRouteErrorResponse(error)) {
    if (error.status === 403) {
      title = "Access denied";
    } else if (error.status === 404) {
      title = "Not found";
    } else {
      title = `Error ${error.status}`;
    }
    detail = typeof error.data === "string" && error.data ? error.data : error.statusText;
  } else if (error instanceof Error) {
    detail = error.message;
  } else if (error) {
    detail = String(error);
  }
  return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full bg-white border border-red-200 rounded-lg shadow-sm p-8">
        <h1 className="text-2xl font-bold text-red-700 mb-3">{title}</h1>
        {detail ? <pre className="bg-red-50 border border-red-100 rounded p-3 text-sm text-red-800 whitespace-pre-wrap break-words mb-6">
            {detail}
          </pre> : <p className="text-gray-600 mb-6">
            The page could not be displayed. Please try again, and contact IT if
            it keeps happening.
          </p>}
        <a href="/dashboard" className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          
          Back to Dashboard
        </a>
      </div>
    </div>;
}
_s3(ErrorBoundary, "oAgjgbJzsRXlB89+MoVumxMQqKM=", false, function () {
  return [useRouteError];
});
_c2 = ErrorBoundary;
export default function App() {
  _s4();
  const data = useLoaderData();
  const csrfToken = data?.csrfToken || "";
  const nonce = data?.nonce || "";
  return <NonceContext.Provider value={nonce}>
      <ThemeProvider>
        <Outlet context={{
        csrfToken
      }} />
      </ThemeProvider>
    </NonceContext.Provider>;
}
_s4(App, "5thj+e1edPyRpKif1JmVRC6KArE=", false, function () {
  return [useLoaderData];
});
_c3 = App;
var _c, _c2, _c3;
$RefreshReg$(_c, "Layout");
$RefreshReg$(_c2, "ErrorBoundary");
$RefreshReg$(_c3, "App");

window.$RefreshReg$ = prevRefreshReg;
window.$RefreshSig$ = prevRefreshSig;